import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, CheckCircle, History, X, Building2, User, Target } from 'lucide-react';
import { DataTable, Column } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { PageHeader } from '@/components/common/PageHeader';
import { Label } from '@/components/common/Label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import { useToast } from '@/components/common/Toast';
import MQLHistoryDialog from './MQLHistoryDialog';
import { mqlService, MQL } from '@/services/api/mqlService';
import apiClient from '@/services/api/apiClient';
import { accountsService } from '@/services/accounts.service';
import { contactsService } from '@/services/contacts.service';
import { usePermissions } from '@/hooks/usePermissions';

interface Account {
  id: string;
  name: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  name?: string;
  accountId?: string;
  account_id?: string;
}

const statusColors = {
  PENDING: 'secondary',
  APPROVED: 'default',
  REJECTED: 'destructive',
  REVIEW: 'outline',
} as const;

export default function MQLPage() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete, canApprove } = usePermissions('mql_gate');

  // ── MQL data ──────────────────────────────────────────────────────────────
  const [mqls, setMqls] = useState<MQL[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMQL, setSelectedMQL] = useState<MQL | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyMQL, setHistoryMQL] = useState<MQL | null>(null);

  // ── Accounts & Contacts ───────────────────────────────────────────────────
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [prefillSource, setPrefillSource] = useState<string>('');

  // ── Form state ────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    accountId: '',
    accountName: '',
    contactId: '',
    contactName: '',
    score: 50,
    notes: '',
    budgetChecked: false,
    authorityChecked: false,
    needChecked: false,
    timelineChecked: false,
  });

  // ── Get current user ID from Zustand auth store ────────────────────────────
  const getCurrentUserId = (): string => {
    try {
      const authData = localStorage.getItem('auth-storage');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed?.state?.user?.id || '';
      }
    } catch {}
    return '';
  };

  // ── Fetch MQLs from API ───────────────────────────────────────────────────
  const fetchMQLs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await mqlService.getMQLs({ page: currentPage, limit: pageSize });
      setMqls(response.data);
      setTotalItems(response.total);
    } catch (error: any) {
      console.error('Error fetching MQLs:', error);
      addToast({ type: 'error', title: 'Failed to load MQLs', description: error.message });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => { fetchMQLs(); }, [fetchMQLs]);

  // ── Load Accounts & Contacts ──────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [accResp, ctcResp] = await Promise.all([
          accountsService.getAccounts({ limit: 1000 }),
          contactsService.getContacts({ limit: 1000 }),
        ]);
        setAccounts(accResp.data);
        const transformed = ctcResp.data.map((c: any) => ({
          ...c,
          name: c.name || `${c.firstName} ${c.lastName}`,
          accountId: c.accountId || c.account_id,
        }));
        setContacts(transformed);
      } catch (err) {
        console.error('Error loading accounts/contacts:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, []);

  // ── Filter contacts by selected account ───────────────────────────────────
  useEffect(() => {
    if (formData.accountId) {
      setFilteredContacts(
        contacts.filter(c => c.accountId === formData.accountId || c.account_id === formData.accountId)
      );
    } else {
      setFilteredContacts([]);
    }
  }, [formData.accountId, contacts]);

  // ── Auto-prefill from journey when contact is selected ───────────────────
  const prefillFromJourney = async (contactId: string) => {
    if (!contactId) return;
    setLoadingPrefill(true);
    try {
      const { data } = await apiClient.get(`/api/mql/prefill/${contactId}`);
      if (data.stageCompleted > 0) {
        setFormData(prev => ({
          ...prev,
          score:            data.score,
          notes:            data.notes || prev.notes,
          budgetChecked:    data.criteria.budget    || prev.budgetChecked,
          authorityChecked: data.criteria.authority || prev.authorityChecked,
          needChecked:      data.criteria.need      || prev.needChecked,
          timelineChecked:  data.criteria.timeline  || prev.timelineChecked,
        }));
        setPrefillSource(`Pre-filled from ${data.stageCompleted} completed ABM stages (ABM score: ${data.score})`);
      } else {
        setPrefillSource('');
      }
    } catch { setPrefillSource(''); }
    finally { setLoadingPrefill(false); }
  };

  // ── Client-side search ────────────────────────────────────────────────────
  const filteredMQLs = useMemo(() => {
    if (!searchQuery) return mqls;
    const q = searchQuery.toLowerCase();
    return mqls.filter(m =>
      m.accountName.toLowerCase().includes(q) ||
      m.contactName.toLowerCase().includes(q) ||
      m.notes.toLowerCase().includes(q)
    );
  }, [searchQuery, mqls]);

  const totalPages = Math.ceil(filteredMQLs.length / pageSize);
  const paginatedMQLs = useMemo(() => {
    if (searchQuery) {
      const start = (currentPage - 1) * pageSize;
      return filteredMQLs.slice(start, start + pageSize);
    }
    return filteredMQLs; // Already paginated from API when no search
  }, [filteredMQLs, currentPage, pageSize, searchQuery]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getCriteriaMet = (criteria: MQL['criteria']) =>
    `${Object.values(criteria).filter(Boolean).length}/4`;

  const resetForm = () => {
    setPrefillSource('');
    setFormData({
      accountId: '', accountName: '', contactId: '', contactName: '',
      score: 50, notes: '',
      budgetChecked: false, authorityChecked: false, needChecked: false, timelineChecked: false,
    });
  };

  // ── CRUD handlers ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      addToast({ type: 'error', title: 'Auth Error', description: 'Cannot determine current user. Please re-login.' });
      return;
    }
    try {
      setSubmitting(true);
      await mqlService.createMQL({
        accountId: formData.accountId,
        contactId: formData.contactId || undefined,
        contactName: formData.contactName,
        score: formData.score,
        notes: formData.notes,
        criteria: {
          budget: formData.budgetChecked,
          authority: formData.authorityChecked,
          need: formData.needChecked,
          timeline: formData.timelineChecked,
        },
        submittedById: userId,
      });
      addToast({ type: 'success', title: 'MQL Submitted', description: `MQL for ${formData.contactName || formData.accountName} submitted for review.` });
      setSubmitDialogOpen(false);
      resetForm();
      fetchMQLs();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Submit Failed', description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedMQL) return;
    try {
      setSubmitting(true);
      await mqlService.updateMQL(selectedMQL.id, {
        accountId: formData.accountId,
        contactId: formData.contactId || undefined,
        contactName: formData.contactName,
        score: formData.score,
        notes: formData.notes,
        criteria: {
          budget: formData.budgetChecked,
          authority: formData.authorityChecked,
          need: formData.needChecked,
          timeline: formData.timelineChecked,
        },
      });
      addToast({ type: 'success', title: 'MQL Updated', description: `MQL for ${formData.contactName || formData.accountName} has been updated.` });
      setEditDialogOpen(false);
      setSelectedMQL(null);
      resetForm();
      fetchMQLs();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Update Failed', description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMQL) return;
    try {
      await mqlService.deleteMQL(selectedMQL.id);
      addToast({ type: 'success', title: 'MQL Deleted', description: `MQL for ${selectedMQL.contactName || selectedMQL.accountName} deleted.` });
      setDeleteDialogOpen(false);
      setSelectedMQL(null);
      fetchMQLs();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Delete Failed', description: error.message });
    }
  };

  const handleApprove = async (mql: MQL) => {
    try {
      await mqlService.updateStatus(mql.id, 'APPROVED', 'Sales Manager');
      addToast({ type: 'success', title: 'MQL Approved', description: `${mql.accountName} approved for sales handoff.` });
      fetchMQLs();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Approve Failed', description: error.message });
    }
  };

  // ── Dialog openers ─────────────────────────────────────────────────────────
  const openEditDialog = (mql: MQL) => {
    setSelectedMQL(mql);
    setFormData({
      accountId: mql.accountId || '',
      accountName: mql.accountName,
      contactId: mql.contactId || '',
      contactName: mql.contactName,
      score: mql.score,
      notes: mql.notes,
      budgetChecked: mql.criteria.budget,
      authorityChecked: mql.criteria.authority,
      needChecked: mql.criteria.need,
      timelineChecked: mql.criteria.timeline,
    });
    setEditDialogOpen(true);
  };

  // ── Column definitions ─────────────────────────────────────────────────────
  const columns: Column<MQL>[] = [
    {
      key: 'accountName', header: 'Account', accessor: 'accountName', sortable: true, width: '18%',
      render: (value) => <div className="font-medium">{value}</div>,
    },
    { key: 'contactName', header: 'Contact', accessor: 'contactName', sortable: true, width: '15%' },
    {
      key: 'submittedDate', header: 'Submitted', accessor: 'submittedDate', sortable: true, width: '12%',
      render: (value) => formatDate(value),
    },
    {
      key: 'score', header: 'Score', accessor: 'score', sortable: true, width: '12%', align: 'center',
      render: (value) => (
        <div className="flex items-center justify-center gap-2">
          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${value}%` }} />
          </div>
          <span className="font-medium">{value}</span>
        </div>
      ),
    },
    {
      key: 'criteria', header: 'Criteria', accessor: 'criteria', width: '10%', align: 'center',
      render: (value) => <span className="font-medium">{getCriteriaMet(value)}</span>,
    },
    {
      key: 'status', header: 'Status', accessor: 'status', sortable: true, width: '12%',
      render: (value: MQL['status']) => <Badge variant={statusColors[value]}>{value}</Badge>,
    },
    {
      key: 'actions', header: 'Actions', accessor: 'id', width: '18%', align: 'center',
      render: (_, mql) => (
        <div className="flex items-center justify-center gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setHistoryMQL(mql); setHistoryDialogOpen(true); }} title="View Engagement History">
            <History className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedMQL(mql); setViewDialogOpen(true); }} title="View Details">
            <Eye className="h-4 w-4" />
          </Button>
          {canApprove && mql.status === 'PENDING' && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleApprove(mql); }} title="Approve MQL">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(mql); }} title="Edit MQL">
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedMQL(mql); setDeleteDialogOpen(true); }}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // ── Shared form body ───────────────────────────────────────────────────────
  const renderFormBody = () => (
    <div className="space-y-6 py-4">
      {loadingData && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          <span>Loading accounts and contacts...</span>
        </div>
      )}

      {/* Account & Contact */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <div className="flex items-center gap-2 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span>Account & Contact</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Account Name <span className="text-destructive">*</span></Label>
            <select
              value={formData.accountId}
              onChange={(e) => {
                const acc = accounts.find(a => a.id === e.target.value);
                setFormData({ ...formData, accountId: e.target.value, accountName: acc?.name || '', contactId: '', contactName: '' });
              }}
              className="w-full px-4 py-2.5 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Account...</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">{accounts.length} accounts available</p>
          </div>
          <div className="space-y-2">
            <Label>Contact Name</Label>
            <select
              value={formData.contactId}
              onChange={(e) => {
                const c = filteredContacts.find(c => c.id === e.target.value);
                setFormData({ ...formData, contactId: e.target.value, contactName: c?.name || '' });
                prefillFromJourney(e.target.value);
              }}
              disabled={!formData.accountId}
              className="w-full px-4 py-2.5 border border-input rounded-md bg-background disabled:bg-muted disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Contact...</option>
              {filteredContacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">
              {!formData.accountId ? '→ Select account first' : filteredContacts.length === 0 ? '⚠️ No contacts for this account' : `${filteredContacts.length} contacts available`}
            </p>
          </div>
        </div>
      </div>

      {/* Lead Score */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            <Target className="h-4 w-4" /><span>Lead Score</span>
          </div>
          <div className={`px-4 py-2 rounded-full border-2 font-bold text-xl ${formData.score >= 80 ? 'bg-green-50 border-green-200 text-green-600' : formData.score >= 60 ? 'bg-blue-50 border-blue-200 text-blue-600' : formData.score >= 40 ? 'bg-yellow-50 border-yellow-200 text-yellow-600' : 'bg-red-50 border-red-200 text-red-600'}`}>
            {formData.score}
          </div>
        </div>
        <input type="range" min="0" max="100" value={formData.score}
          onChange={(e) => setFormData({ ...formData, score: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span className="text-red-600">0 – Cold</span>
          <span className="text-yellow-600">50 – Warm</span>
          <span className="text-green-600">100 – Hot</span>
        </div>
      </div>

      {/* BANT */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            <CheckCircle className="h-4 w-4" /><span>BANT Criteria</span>
          </div>
          <span className="text-sm font-medium">
            {[formData.budgetChecked, formData.authorityChecked, formData.needChecked, formData.timelineChecked].filter(Boolean).length}/4 Complete
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {[
            { key: 'budgetChecked', label: 'Budget', desc: 'Has allocated budget' },
            { key: 'authorityChecked', label: 'Authority', desc: 'Decision maker identified' },
            { key: 'needChecked', label: 'Need', desc: 'Clear business need exists' },
            { key: 'timelineChecked', label: 'Timeline', desc: 'Purchase timeline defined' },
          ].map(({ key, label, desc }) => (
            <label key={key} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
              <input type="checkbox"
                checked={formData[key as keyof typeof formData] as boolean}
                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary cursor-pointer"
              />
              <div>
                <div className="font-medium">{label}</div>
                <div className="text-xs text-muted-foreground">{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Prefill notice */}
      {prefillSource && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 text-xs text-violet-700 dark:text-violet-400">
          <span>✦</span>
          <span>{prefillSource} — review and adjust below</span>
        </div>
      )}
      {loadingPrefill && (
        <div className="text-xs text-muted-foreground animate-pulse">Loading journey data…</div>
      )}

      {/* Notes */}
      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <textarea value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={4} placeholder="Add qualification notes, budget details, timeline, etc."
          className="w-full px-4 py-3 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageHeader
        title="MQL Gate"
        description="Submit and manage Marketing Qualified Leads for sales handoff"
        actions={
          canCreate ? (
            <Button onClick={() => { resetForm(); setSubmitDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Submit MQL
            </Button>
          ) : undefined
        }
      />

      <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search MQLs by account, contact, or notes..." />

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
        </div>
      ) : (
        <DataTable data={paginatedMQLs} columns={columns} onRowClick={(mql) => navigate(`/mql/${mql.id}`)} />
      )}

      {filteredMQLs.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={searchQuery ? totalPages : Math.ceil(totalItems / pageSize)}
          pageSize={pageSize}
          totalItems={searchQuery ? filteredMQLs.length : totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
          pageSizeOptions={[5, 10, 20, 50]}
        />
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete MQL</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the MQL for <span className="font-semibold">{selectedMQL?.contactName || selectedMQL?.accountName}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogClose onClose={() => setSubmitDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle className="text-2xl">Submit MQL</DialogTitle>
            <DialogDescription>Submit a Marketing Qualified Lead for sales team review</DialogDescription>
          </DialogHeader>
          {renderFormBody()}
          <DialogFooter className="bg-muted/30 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
            <Button variant="outline" onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!formData.accountId || submitting} className="gap-2">
              <Plus className="h-4 w-4" />{submitting ? 'Submitting...' : 'Submit MQL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogClose onClose={() => setEditDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Edit MQL</DialogTitle>
            <DialogDescription>Update MQL details and qualification criteria</DialogDescription>
          </DialogHeader>
          {renderFormBody()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={!formData.accountId || submitting}>
              {submitting ? 'Saving...' : 'Update MQL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogClose onClose={() => setViewDialogOpen(false)} />
          <DialogHeader><DialogTitle>MQL Details</DialogTitle></DialogHeader>
          {selectedMQL && (
            <div className="space-y-4 py-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Account</Label><p className="font-medium">{selectedMQL.accountName}</p></div>
                <div><Label>Contact</Label><p className="font-medium">{selectedMQL.contactName}</p></div>
                <div><Label>Submitted Date</Label><p>{formatDate(selectedMQL.submittedDate)}</p></div>
                <div><Label>Status</Label><Badge variant={statusColors[selectedMQL.status]}>{selectedMQL.status}</Badge></div>
                <div>
                  <Label>Lead Score</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-24 h-2 bg-muted rounded-full"><div className="h-full bg-primary rounded-full" style={{ width: `${selectedMQL.score}%` }} /></div>
                    <span className="font-medium">{selectedMQL.score}</span>
                  </div>
                </div>
                <div><Label>Criteria Met</Label><p className="font-medium">{getCriteriaMet(selectedMQL.criteria)}</p></div>
              </div>
              <div>
                <Label>BANT Criteria</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(selectedMQL.criteria).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      {v ? <CheckCircle className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-600" />}
                      <span className="capitalize">{k}</span>
                    </div>
                  ))}
                </div>
              </div>
              {selectedMQL.notes && <div><Label>Notes</Label><p className="text-sm text-muted-foreground mt-1">{selectedMQL.notes}</p></div>}
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Submitted By</Label><p>{selectedMQL.submittedBy}</p></div>
                {selectedMQL.reviewedBy && <div><Label>Reviewed By</Label><p>{selectedMQL.reviewedBy}</p></div>}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setViewDialogOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      {historyMQL && (
        <MQLHistoryDialog
          open={historyDialogOpen}
          onClose={() => { setHistoryDialogOpen(false); setHistoryMQL(null); }}
          mql={historyMQL}
        />
      )}
    </div>
  );
}
